<script setup lang="ts">
import { computed  } from 'vue'
import { employeeArchiveCheckOptions, useEmployeeArchiveMutation } from '../../composables/useEmployee'
import { useEmployeeArchiveDialog } from '../../composables/useEmployeeArchiveDialog'
import { Site } from '../../../sites/models/site'
import { useQuery } from '@tanstack/vue-query'
import { DateTime } from 'luxon'
import { useI18n } from 'vue-i18n'
import { EmployeeArchiveCheck } from '../../models/employeeArchiveCheck'

const { site } = defineProps<{
  site: Site
}>()

const {
    hide,
    isShown,
    employee,
} = useEmployeeArchiveDialog()

const employeeId = computed(() => employee.value?.id ?? null)

const enabled = computed(() => employee.value !== undefined && employee.value !== null)

const {
  data,
  isFetching: isArchiveCheckFetching,
} = useQuery<{ check: EmployeeArchiveCheck }, Error>({
    ...employeeArchiveCheckOptions(() => site.id, employeeId),
    enabled: enabled,
})
const canArchive = computed(() => data?.value?.check.canBeArchived ?? false)
const shifts = computed(() => data?.value?.check.shifts ?? [])

const {
  mutate: archive,
  isPending: isArchiving,
} = useEmployeeArchiveMutation(() => site.id)

const archivePressed = () => {
  if (employee.value) {
    archive({
      employeeId: employee.value.id
    }, {
      onSuccess: () => {
        hide()
      }
    })
  }
}

const { t } = useI18n()
</script>

<template>
  <Dialog
    :visible="isShown"
    header="Archive Employees"
    dismissable-mask
    modal
    class="w-full max-w-2xl md:w-1/2"
    @update:visible="hide"
  >
    <ProgressSpinner
      v-if="isArchiveCheckFetching"
      class="w-full mx-auto"
    />
    
    <div
      v-else
      class="flex flex-col gap-y-4 m-1"
    >
      <Message
        v-if="shifts.length > 0"
        severity="info"
      >
        <div
          class="flex flex-col gap-4"
        >
          <div
            class="text-color font-medium"
          >
            {{ t('employees.archive_help_description') }}
          </div>
        </div>
      </Message>

      <Message
        v-if="shifts.length > 0"
        severity="warn"
      >
        <div
          class="flex flex-col gap-4"
        >
          <div
            class="text-color font-semibold"
          >
            {{ t('employees.archive_conflict_title') }}
          </div>
          
          <div
            class="text-color font-medium"
          >
            {{ t('employees.archive_conflict_description') }}
          </div>
        </div>
      </Message>

      <div
        v-if="employee"
        class="flex items-center"
      >
        <Avatar
          :label="employee.firstName.charAt(0) + employee.lastName.charAt(0)"
          :image="employee.avatarUrl"
          class="bg-highlight"
          shape="circle"
          size="large"
        />
        <div class="ml-4">
          <div class="font-medium">
            {{ employee.firstName }} {{ employee.lastName }}
          </div>
          <div class="text-muted-color">
            {{ employee.email }}
          </div>
        </div>
      </div>
      <div
        v-else
      >
        No employee selected
      </div>

      <div
        v-for="shift in shifts"
        :key="shift.id"
      >
        <div>
          <span>Shift ID: {{ shift.id }}</span>
        </div>
        <div>
          <span>Start: {{ shift.startDate.toLocaleString(DateTime.DATETIME_MED) }}</span>
        </div>
        <div>
          <span>End: {{ shift.endDate.toLocaleString(DateTime.DATETIME_MED) }}</span>
        </div>

        <Divider />
      </div>

      <div
        class="flex justify-end gap-2"
      >
        <Button label="Cancel" text @click="hide" />
        <Button
          label="Archive"
          :severity="canArchive ? 'danger' : 'secondary'"
          :disabled="!canArchive"
          :loading="isArchiving"
          @click="archivePressed"
        />
      </div>
    </div>
  </Dialog>
</template>